import { all, put } from 'redux-saga/effects';
import { isLoaderComponent } from '../Components/__helpers__/component.helpers';
import { ComponentTypes } from '../Data/Components';
import { range } from '../Utils/utils';

export const SUBCATEGORY_MAX_MATCHES_DEFAULT = 5;

export function getMaxMatchesToDisplay({
  subcategoryMaxMatches,
  componentMaxMatches,
}) {
  if (!subcategoryMaxMatches && !componentMaxMatches) {
    // 5 is a sensible default if we have neither of these values which can only
    // happen if there are partial failures such as lead platform is down
    return SUBCATEGORY_MAX_MATCHES_DEFAULT;
  }

  if (!subcategoryMaxMatches) {
    return componentMaxMatches;
  }

  if (!componentMaxMatches) {
    return subcategoryMaxMatches;
  }

  return Math.min(subcategoryMaxMatches, componentMaxMatches);
}

/**
 * Generator function to set supplier match options and mark them as selected.
 * This function yields dispatch actions to update options, selected options, and values for a supplier match component.
 * @param {Object} supplierMatchComponent - The supplier match component object.
 * @param {Array} supplierMatches - Array of supplier matches to convert into options.
 */
export function* setSupplierMatchOptionsAndMarkSelected(
  supplierMatchComponent,
  supplierMatches,
  subcategoryMaxMatches
) {
  const maxMatchesToDisplay = getMaxMatchesToDisplay({
    subcategoryMaxMatches,
    componentMaxMatches: supplierMatchComponent.component.maxMatches,
  });

  const supplierMatchOptions = supplierMatches.map(convertMatchToOption);
  if (
    maxMatchesToDisplay &&
    maxMatchesToDisplay < supplierMatchOptions.length
  ) {
    supplierMatchOptions.length = maxMatchesToDisplay;
  }

  // Set all options to show and make sure that they are all selected.
  yield all([
    put({
      type: 'COMPONENTS/setOptions',
      payload: {
        componentId: supplierMatchComponent.component.id,
        options: supplierMatchOptions,
      },
    }),
    put({
      type: 'COMPONENTS/setSelectedOptions',
      payload: {
        componentId: supplierMatchComponent.component.id,
        selectedOptions: supplierMatchOptions.map((option) => option.id),
      },
    }),
    put({
      type: 'COMPONENTS/updateValue',
      payload: {
        componentId: supplierMatchComponent.component.id,
        value: supplierMatchOptions.map((option) => option.key).join(','),
      },
    }),
  ]);
}

/**
 * Function to convert a match object to an input option.
 * @param {Object} match - Match object containing brandFilterValue, brandLabel, brandLogoUrl, webformComponentId, and optionId.
 * @returns {Object} - Converted input option object.
 */
export const convertMatchToOption = (match) => {
  if (match.brandId) {
    return {
      key: match.brandLabel,
      value: match.brandLabel,
      icon_url: match.brandLogoUrl,
      webform_component_id: match.webformComponentId,
      id: match.brandId,
    };
  }

  return {
    key: match.brandFilterValue,
    value: match.brandLabel,
    icon_url: match.brandLogoUrl,
    webform_component_id: match.webformComponentId,
    id: match.optionId,
  };
};

export const convertPartialSubmissionToInputAnswers = (
  partialSubmission,
  supplierMatchComponents,
  subcategoryMatches
) => {
  const supplierMatchMappedQuestionIds = supplierMatchComponents.map(
    (supplierMatchComponent) =>
      supplierMatchComponent.component.mappedQuestionId
  );
  const supplierMatchAnswerValue = subcategoryMatches
    .map((match) => (match.brandId ? match.brandLabel : match.brandFilterValue))
    .join(',');

  const answeredQuestions = partialSubmission.filter(
    (answer) =>
      answer.value &&
      answer.mappedQuestionId &&
      answer.value.length !== 0 &&
      answer.mappedQuestionId.replace(/\s+/, '') !== ''
  );
  return answeredQuestions.map((answer) => {
    const isSupplierMatchQuestion = supplierMatchMappedQuestionIds.includes(
      answer.mappedQuestionId
    );

    let answerValue;
    if (isSupplierMatchQuestion) {
      answerValue = supplierMatchAnswerValue;
    } else if (typeof answer.value === 'object') {
      answerValue = answer.value.join(',');
    } else {
      answerValue = answer.value;
    }

    return {
      questionId: answer.mappedQuestionId,
      answerValue,
    };
  });
};

export const getTriggerPageForGetCustomerMatchesOrNull = ({
  components,
  showCustomerMatchesOnThankYouPage,
}) => {
  if (
    !Object.values(components).some(
      (component) => component.component.type === 'supplier-match'
    ) &&
    !showCustomerMatchesOnThankYouPage
  ) {
    return null;
  }

  const postcodeComponentPages = Object.values(components)
    .filter(
      (component) =>
        component.component.mappedQuestionId === 'primary_address_postalcode'
    )
    .map((component) => component.component.page);

  return postcodeComponentPages.length > 0
    ? Math.max(...postcodeComponentPages)
    : null;
};

const isLoaderBetweenTriggerAndMatchesShown = (
  component,
  triggerPage,
  firstSupplierMatchPage
) => {
  const componentPage = component.component.page;
  return (
    isLoaderComponent(component) &&
    componentPage > triggerPage &&
    componentPage < firstSupplierMatchPage
  );
};

/**
 * Determines the last loader page that appears before the first supplier match page in a given sequence of pages.
 *
 * @param {number} customerMatchesTriggerPage - The page number after which a request to get customer matches is triggered.
 * @param {Object} components - An object consisting of components keyed by component id.
 * @param {number} firstSupplierMatchPage - The first page number where a supplier match component is displayed.
 * @returns {number|null} The page number of the last loader before the supplier match page, or `null` if no such loader exists.
 *
 * This function identifies if there is a loader component that appears between the trigger page for customer matches and the first supplier match page. If such loaders exist, it returns the page number of the last loader. If no loaders are found, it returns `null`.
 *
 * Example:
 * getFinalLoaderPageBeforeSupplierMatches(3, components, 7)
 * // Returns the page number of the last loader page before page 7, or null if no loaders are found.
 */
const getFinalLoaderPageBeforeSupplierMatches = (
  customerMatchesTriggerPage,
  components,
  firstSupplierMatchPage
) => {
  const loaderPages = Object.values(components)
    .filter((component) =>
      isLoaderBetweenTriggerAndMatchesShown(
        component,
        customerMatchesTriggerPage,
        firstSupplierMatchPage
      )
    )
    .map((component) => component.component.page);

  // Return the last loader page found or null if none exist
  return loaderPages.length > 0 ? Math.max(...loaderPages) : null;
};

/**
 * Determines which pages should be blocked from navigation during a customer matches request process.
 *
 * @param {Object} components - An object consisting of components keyed by component id.
 * @param {number[]} potentialSubmissionPages - An array of page numbers representing pages where there is a possibility that based on a customers choices, clicking on the cta button could lead to a submission.
 * @returns {number[]} An array of page numbers that should be blocked from navigation during the customer matches request, based on the presence of supplier match components, loaders, and potential submission pages.
 */
export const getPagesBlockedDuringCustomerMatchesRequest = ({
  components,
  potentialSubmissionPages,
  showCustomerMatchesOnThankYouPage,
}) => {
  const supplierMatchPages = Object.values(components)
    .filter(isSupplierMatch)
    .map((component) => component.component.page);

  if (supplierMatchPages.length === 0) return [];

  const customerMatchesTriggerPage = getTriggerPageForGetCustomerMatchesOrNull({
    components,
    showCustomerMatchesOnThankYouPage,
  });

  if (customerMatchesTriggerPage === null) return [];

  // We require the result of the customer matches request for any page which can show either a supplier match component or reveals the match in a legal statement.
  const pagesRequiringCustomerMatches = [
    ...new Set([...supplierMatchPages, ...potentialSubmissionPages]),
  ].sort();

  const firstSupplierMatchPage = Math.min(...supplierMatchPages);

  // If there is a loader before a supplier match we prevent navigation at this point so that the customer can see a loading animation during this time.
  const finalLoaderPageBeforeSupplierMatches =
    getFinalLoaderPageBeforeSupplierMatches(
      customerMatchesTriggerPage,
      components,
      firstSupplierMatchPage
    );

  const firstPotentialSubmissionPage = Math.min(...potentialSubmissionPages);

  // If the loader is before potential submission pages we will prevent navigation past that point
  if (
    finalLoaderPageBeforeSupplierMatches !== null &&
    finalLoaderPageBeforeSupplierMatches < firstPotentialSubmissionPage
  ) {
    const finalPotentialSubmissionPage = Math.max(...potentialSubmissionPages);
    return range(
      finalLoaderPageBeforeSupplierMatches + 1,
      finalPotentialSubmissionPage
    );
  }

  // Otherwise prevent navigation to any pages requiring a customer match using a spinner wheel in the navigation bar.
  return pagesRequiringCustomerMatches;
};

export const getSupplierMatchComponent = ({ components }) => {
  return Object.values(components).find((component) =>
    isSupplierMatch(component)
  );
};

export const isSupplierMatch = (component) => {
  return component?.component?.type === ComponentTypes.SUPPLIER_MATCH;
};

export const hasSingleOption = (component) => {
  const matches = component?.input?.options;
  return typeof matches === 'object' && matches.length === 1;
};

export const hasMultipleOptions = (component) => {
  const matches = component?.input?.options;
  return typeof matches === 'object' && matches.length > 1;
};

export const getSupplierMatchDisplayValues = (component) =>
  component?.input?.options?.map((option) => option.value);

export const isMatchImpliedByPrivacyStatement = (
  hideOnSingleMatch,
  maxMatches,
  matches,
  supplierMatchSelectionEnabled
) =>
  Boolean(
    supplierMatchSelectionEnabled &&
      hideOnSingleMatch &&
      (maxMatches === '1' || matches.length === 1)
  );

export const shouldLegalStatementBeAboveNavigation = (
  matches,
  maxMatchesToDisplay
) => {
  const shownMatches =
    maxMatchesToDisplay && maxMatchesToDisplay < matches.length
      ? matches.slice(0, maxMatchesToDisplay)
      : matches;

  return shownMatches.some((match) => match.requiresLegalStatementAboveNav);
};
